import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Download, Trash2 } from 'lucide-react'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useTask } from '@/features/tasks/hooks'
import { TaskDetailDrawer } from '@/features/tasks/components/TaskDetailDrawer'
import { downloadFile, useContractFiles, useDeleteFile } from './hooks'
import type { FileAttachment } from './types'
import { FileThumbnail } from './components/FileThumbnail'
import { FileUploadButton } from './components/FileUploadButton'
import { TaskSourceMarker } from './components/TaskSourceMarker'

const TABLE_ID_PREFIX = 'contract-files'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL')
  } catch {
    return iso
  }
}

export function FilesTab({ contractId, configurationCode }: { contractId: number; configurationCode: string }) {
  const confirm = useConfirm()
  const filesQ = useContractFiles(contractId)
  const typesQ = useDocumentTypes(configurationCode, false)
  const del = useDeleteFile(contractId)
  const tableId = `${TABLE_ID_PREFIX}-${contractId}`
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(tableId)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const taskQ = useTask(selectedTaskId)

  const typeLabelById = useMemo(() => {
    const m = new Map<number, string>()
    typesQ.data?.forEach(t => m.set(t.id, t.labelPl))
    return m
  }, [typesQ.data])

  const columns: ColumnDef<FileAttachment>[] = useMemo(
    () => [
      {
        key: 'thumbnail',
        header: '',
        accessor: (f) => (
          <FileThumbnail
            contractId={contractId}
            fileId={f.id}
            isImage={f.isImage}
            source={f.source}
            taskRefId={f.taskRef?.id}
            thumbnailUrl={f.thumbnailUrl}
            filename={f.originalFilename}
          />
        ),
        className: 'w-16',
        alwaysVisible: true,
      },
      {
        key: 'originalFilename',
        header: 'Nazwa pliku',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj nazwę...' },
        accessor: (f) => (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                downloadFile(contractId, f.id).catch((err) => toast.error(formatError(err)))
              }}
              className="text-sky-700 hover:underline truncate max-w-md text-left"
              title={f.originalFilename}
            >
              {f.originalFilename}
            </button>
            {f.source === 'task' && f.taskRef && (
              <TaskSourceMarker taskRef={f.taskRef} onClick={setSelectedTaskId} />
            )}
          </div>
        ),
        alwaysVisible: true,
      },
      {
        key: 'documentTypeId',
        header: 'Typ dokumentu',
        sortable: true,
        filter: {
          kind: 'select',
          multi: true,
          options: (typesQ.data ?? []).map(t => ({ value: String(t.id), label: t.labelPl })),
        },
        accessor: (f) => f.documentTypeId
          ? <span className="rounded bg-sky-50 text-sky-800 text-xs px-2 py-0.5">{typeLabelById.get(f.documentTypeId) ?? `#${f.documentTypeId}`}</span>
          : <span className="text-slate-400 italic">-</span>,
        className: 'whitespace-nowrap',
      },
      {
        key: 'contentType',
        header: 'Format',
        sortable: true,
        filter: { kind: 'text' },
        accessor: (f) => <span className="font-mono text-xs text-slate-500">{f.contentType}</span>,
      },
      {
        key: 'sizeBytes',
        header: 'Rozmiar',
        sortable: true,
        accessor: (f) => formatSize(f.sizeBytes),
        className: 'tabular-nums whitespace-nowrap',
      },
      {
        key: 'description',
        header: 'Opis',
        accessor: (f) => f.description ?? <span className="text-slate-400">-</span>,
      },
      {
        key: 'uploadedAt',
        header: 'Dodano',
        sortable: true,
        filter: { kind: 'dateRange' },
        accessor: (f) => <span className="text-xs text-muted-foreground">{formatDateTime(f.uploadedAt)}</span>,
        className: 'whitespace-nowrap',
      },
      {
        key: 'uploadedByUser',
        header: 'Autor',
        accessor: (f) => `#${f.uploadedByUser}`,
        className: 'whitespace-nowrap',
      },
      {
        key: 'actions',
        header: '',
        accessor: (f) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                downloadFile(contractId, f.id).catch((err) => toast.error(formatError(err)))
              }}
              className="text-slate-600 hover:text-sky-700"
              title="Pobierz"
            >
              <Download className="size-4" />
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: 'Usunąć plik?',
                  description: `"${f.originalFilename}" zostanie trwale usunięty.`,
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(f.id, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-red-600 hover:text-red-800"
              title="Usuń"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ),
        className: 'w-24',
        alwaysVisible: true,
      },
    ],
    [contractId, confirm, del, typesQ.data, typeLabelById, setSelectedTaskId],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(filesQ.data ?? [], columns, state, {
        valueGetters: {
          originalFilename: (f) => f.originalFilename,
          documentTypeId: (f) => f.documentTypeId != null ? String(f.documentTypeId) : '',
          contentType: (f) => f.contentType,
          sizeBytes: (f) => f.sizeBytes,
          description: (f) => f.description ?? '',
          uploadedAt: (f) => f.uploadedAt.slice(0, 10),
          uploadedByUser: (f) => f.uploadedByUser,
        },
        searchString: (f) => {
          const typeLabel = f.documentTypeId ? typeLabelById.get(f.documentTypeId) ?? '' : ''
          return `${f.originalFilename} ${typeLabel} ${f.contentType} ${f.description ?? ''}`.toLowerCase()
        },
      }),
    [filesQ.data, columns, state, typeLabelById],
  )

  return (
    <div className="flex flex-col w-full p-4 gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Załączniki (PDF/DOC/Excel + obrazki) - max 50 MB. Linki do pobrania ważne 5 min.
        </p>
        <FileUploadButton contractId={contractId} configurationCode={configurationCode} />
      </div>

      <DataTable<FileAttachment>
        tableId={tableId}
        data={filteredRows}
        columns={columns}
        rowKey={(f) => f.id}
        isLoading={filesQ.isLoading}
        isError={filesQ.isError}
        sort={state.sort}
        onSortChange={setSort}
        state={state}
        searchable
        searchPlaceholder="Szukaj nazwy / typu / opisu..."
        onSearchChange={setSearch}
        onFilterChange={setFilter}
        onFilterRemove={removeFilter}
        onClearAll={clearFilters}
        emptyState="Brak plików. Kliknij 'Dodaj plik' żeby załączyć pierwszy."
      />

      <TaskDetailDrawer
        task={taskQ.data ?? null}
        onClose={() => setSelectedTaskId(null)}
      />
    </div>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd'
}
